import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { Room, Etage } from '../types';
import { cacheService } from '../../functions/services/cacheService';

interface FloorData {
  selectedRooms: string[];
  roomDetails: Record<string, { customName?: string; notes?: string }>;
  items: { itemId: string; quantity: number; roomId: string; isCustom?: boolean; unitPrice?: number }[];
}

interface ProjectDetailsProps {
  floorSelections: Record<string, FloorData>;
  rooms: Room[];
  etages: Etage[];
  hidePricing?: boolean;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ floorSelections, rooms, etages, hidePricing = false }) => {
  const theme = useTheme();

  const getFloorName = (floorId: string) => {
    const etage = etages.find(e => e.id === floorId);
    return etage ? etage.name : floorId;
  };

  const getItemName = (itemId: string) => {
    const item = cacheService.getItems().find(item => item.id === itemId);
    return item ? item.name : itemId;
  };

  return (
    <Box className="project-details" sx={{ mb: 4 }}>
      <Typography 
        variant="h5" 
        sx={{ 
          fontWeight: 600,
          mb: 3,
          color: theme.palette.primary.main,
          borderBottom: `2px solid ${theme.palette.primary.main}`,
          pb: 1
        }}
      >
        Détails du projet
      </Typography>

      {Object.entries(floorSelections)
        .filter(([_, data]) => {
          return data.items.some(item => {
            const quantity = item.quantity || 0;
            const unitPrice = item.unitPrice || 0;
            return quantity * unitPrice > 0;
          });
        })
        .map(([floorId, data]) => {
          const floorTotal = data.items.reduce((sum, item) => {
            const quantity = item.quantity || 0;
            const unitPrice = item.unitPrice || 0;
            return sum + (quantity * unitPrice);
          }, 0);

          return (
            <Box key={floorId} className="project-details" sx={{ mb: 4 }}>
              <TableContainer sx={{ 
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[1]
              }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 600,
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        fontSize: '1.1rem'
                      }}>Étage / Pièce / Item</TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 600,
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        fontSize: '1.1rem'
                      }}>Quantité</TableCell>
                      {!hidePricing && (
                        <TableCell align="right" sx={{ 
                          fontWeight: 600,
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          fontSize: '1.1rem'
                        }}>Prix Unitaire</TableCell>
                      )}
                      <TableCell align="right" sx={{ 
                        fontWeight: 600,
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        fontSize: '1.1rem'
                      }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Floor Row */}
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 700,
                          backgroundColor: theme.palette.grey[200],
                          pl: 2,
                          fontSize: '1.1rem',
                          borderBottom: `2px solid ${theme.palette.grey[300]}`
                        }}
                      >
                        {getFloorName(floorId)}
                      </TableCell>
                      <TableCell 
                        colSpan={hidePricing ? 1 : 2} 
                        sx={{ 
                          backgroundColor: theme.palette.grey[200],
                          borderBottom: `2px solid ${theme.palette.grey[300]}`
                        }}
                      />
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 700,
                          backgroundColor: theme.palette.grey[200],
                          fontSize: '1.1rem',
                          borderBottom: `2px solid ${theme.palette.grey[300]}`
                        }}
                      >
                        ${floorTotal.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    {/* Room Rows */}
                    {data.selectedRooms
                      .filter(roomId => {
                        const roomItems = data.items.filter(item => item.roomId === roomId);
                        return roomItems.some(item => {
                          const quantity = item.quantity || 0;
                          const unitPrice = item.unitPrice || 0;
                          return quantity * unitPrice > 0;
                        });
                      })
                      .map((roomId) => {
                        const room = rooms.find(r => r.id === roomId);
                        const roomDetail = data.roomDetails[roomId];
                        const roomItems = data.items.filter(item => item.roomId === roomId);
                        const roomTotal = roomItems.reduce((sum, item) => {
                          const quantity = item.quantity || 0;
                          const unitPrice = item.unitPrice || 0;
                          return sum + (quantity * unitPrice);
                        }, 0);

                        return (
                          <React.Fragment key={roomId}>
                            <TableRow>
                              <TableCell 
                                sx={{ 
                                  pl: 4,
                                  fontWeight: 600,
                                  backgroundColor: theme.palette.grey[100],
                                  fontSize: '1rem',
                                  borderLeft: `4px solid ${theme.palette.primary.light}`
                                }}
                              >
                                {roomDetail?.customName || room?.name}
                              </TableCell>
                              <TableCell 
                                colSpan={hidePricing ? 1 : 2} 
                                sx={{ 
                                  backgroundColor: theme.palette.grey[100]
                                }}
                              />
                              <TableCell 
                                align="right"
                                sx={{ 
                                  fontWeight: 600,
                                  backgroundColor: theme.palette.grey[100]
                                }}
                              >
                                ${roomTotal.toFixed(2)}
                              </TableCell>
                            </TableRow>
                            {/* Item Rows */}
                            {roomItems
                              .filter(item => {
                                const quantity = item.quantity || 0;
                                const unitPrice = item.unitPrice || 0;
                                return quantity * unitPrice > 0;
                              })
                              .map((item, index) => {
                                const quantity = item.quantity || 0;
                                const unitPrice = item.unitPrice || 0;
                                const itemTotal = quantity * unitPrice;

                                return (
                                  <TableRow 
                                    key={`${roomId}-${item.itemId}-${index}`}
                                    sx={{
                                      '&:hover': {
                                        backgroundColor: theme.palette.grey[50]
                                      }
                                    }}
                                  >
                                    <TableCell 
                                      sx={{ 
                                        pl: 8,
                                        fontSize: '0.95rem',
                                        borderLeft: `4px solid ${theme.palette.primary.light}`,
                                        opacity: 0.9
                                      }}
                                    >
                                      {getItemName(item.itemId)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.95rem' }}>
                                      {quantity}
                                    </TableCell>
                                    {!hidePricing && (
                                      <TableCell align="right" sx={{ fontSize: '0.95rem' }}>
                                        ${unitPrice.toFixed(2)}
                                      </TableCell>
                                    )}
                                    <TableCell align="right" sx={{ fontSize: '0.95rem' }}>
                                      {hidePricing ? '' : `$${itemTotal.toFixed(2)}`}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </React.Fragment>
                        );
                      })}
                    {/* Floor Total */}
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          fontWeight: 700,
                          backgroundColor: theme.palette.grey[200],
                          fontSize: '1.1rem',
                          borderTop: `2px solid ${theme.palette.grey[300]}`
                        }}
                      >
                        Total {getFloorName(floorId)}
                      </TableCell>
                      <TableCell 
                        colSpan={hidePricing ? 1 : 2} 
                        sx={{ 
                          backgroundColor: theme.palette.grey[200],
                          borderTop: `2px solid ${theme.palette.grey[300]}`
                        }}
                      />
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 700,
                          backgroundColor: theme.palette.grey[200],
                          fontSize: '1.1rem',
                          borderTop: `2px solid ${theme.palette.grey[300]}`
                        }}
                      >
                        ${floorTotal.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
    </Box>
  );
};

export default ProjectDetails; 